import { useEffect, useState } from 'react';
import { CopyIcon } from './styled';
import loadable from '@loadable/component';
import useCopyUrlToClipboard from '@/pages/CompanyReport/hooks/useCopyUrlToClipboard';
import { SidebarActions } from '@/pages/CompanyReport/components/Overview/components/SidebarActions';
import { useAnalysis, useSummaryV2 } from '@/hooks/useCompany';
import { useTranslation } from '@/hooks/useTranslation';
import { Box, Text } from '@simplywallst/ui-core';

const Snowflake = loadable(() => import('./Snowflake'), {
  resolveComponent: (components) => components.Default,
});

function targetIsDocument(target: EventTarget | null): target is Document {
  if (target) {
    return target instanceof Document;
  }
  return false;
}

const minY = 50;
const maxY = 220;

interface Props {
  onClick?: (route: string) => void;
  keepSnowflakePinned?: boolean;
}
export function FadingSnowflake({ onClick, keepSnowflakePinned }: Props) {
  const [opacity, setOpacity] = useState(0);
  const copyUrlToClipboard = useCopyUrlToClipboard();
  const summary = useSummaryV2();
  const analysis = useAnalysis();

  const t = useTranslation();

  useEffect(() => {
    const onScroll = (event: Event) => {
      const { target } = event;
      if (targetIsDocument(target)) {
        const { scrollTop } = target.documentElement;
        const minDiff = scrollTop - minY;
        const maxDiff = maxY - minY;
        setOpacity(Math.min((minDiff < 0 ? 0 : minDiff / maxDiff) * 1, 1));
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (summary.status !== 'success') {
    return null;
  }

  const visibility = keepSnowflakePinned ? 1 : opacity;

  return (
    <>
      <Box
        data-cy-id="desktop-sidebar-snowflake"
        style={{
          opacity: visibility,
          clipPath: `inset(${100 - visibility * 100}% 0 0 0)`,
        }}
      >
        <Box paddingBottom="x0_5">
          <Snowflake points={summary.score} onClick={onClick} />
        </Box>
      </Box>

      <Box
        data-cy-id="desktop-sidebar-snowflake"
        style={{
          opacity: visibility,
        }}
      >
        <Box paddingBottom="x1_5">
          <Text
            typography="smallStrong"
            isTruncated
            data-cy-id="desktop-sidebar-company-name"
          >
            {summary.name}
            <span>
              <CopyIcon onClick={copyUrlToClipboard} width={24} height={24} />
            </span>
          </Text>
          <Text
            typography="xSmall"
            color="textSoft"
            data-cy-id="desktop-sidebar-title"
          >
            {t('page.companyReport.header.companyTitle', {
              uniqueSymbol: summary.uniqueSymbol,
            })}
          </Text>
          {analysis.status === 'success' ? (
            <Box paddingTop="x0_25">
              <Text
                typography="tiny"
                color="textSoft"
                data-cy-id="desktop-sidebar-market-cap"
              >
                {t('page.companyReport.header.metadata.marketCapShort', {
                  marketCap: analysis.formattedMarketCap,
                })}
              </Text>
            </Box>
          ) : null}
        </Box>
        <SidebarActions />
      </Box>
    </>
  );
}
